import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  BarChart3,
  CalendarDays,
  Search,
  Star,
  HelpCircle,
  Menu,
  LayoutGrid,
  Building2,
  Map,
  TrendingUp,
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/theme-toggle'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet'

const navigation = [
  { name: "Dashboard", href: "/", icon: BarChart3 },
  { name: "Stocks", href: "/stocks", icon: TrendingUp },
  { name: "Categories", href: "/categories", icon: LayoutGrid },
  { name: "Releases", href: "/releases", icon: CalendarDays },
  { name: "Search", href: "/search", icon: Search },
  { name: "Sources", href: "/sources", icon: Building2 },
  { name: "Regional", href: "/regional", icon: Map },
  { name: "Favorites", href: "/favorites", icon: Star },
  { name: "Help", href: "/help", icon: HelpCircle },
]

function isActiveRoute(pathname: string, href: string) {
  if (href === '/') return pathname === '/'
  return pathname.startsWith(href)
}

export function SiteHeader() {
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/80 backdrop-blur-sm">
      <div className="container flex h-14 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="flex h-7 w-7 items-center justify-center rounded bg-primary">
            <BarChart3 className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="text-sm font-semibold tracking-tight">
            US Economic
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-0.5 ml-8">
          {navigation.map((item) => {
            const active = isActiveRoute(pathname, item.href)
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  "relative px-3 py-1.5 text-sm transition-colors",
                  active
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                {item.name}
                {active && (
                  <span className="absolute bottom-[-13px] left-0 right-0 h-[2px] bg-primary rounded-full" />
                )}
              </Link>
            )
          })}
        </nav>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right Actions */}
        <div className="flex items-center gap-1">
          <ThemeToggle />

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Menu className="h-4 w-4" />
                <span className="sr-only">打开菜单</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[260px] p-0 bg-background border-l border-border">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-4 h-14 border-b border-border">
                  <Link
                    to="/"
                    className="flex items-center gap-2"
                    onClick={() => setOpen(false)}
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded bg-primary">
                      <BarChart3 className="h-4 w-4 text-primary-foreground" />
                    </div>
                    <span className="text-sm font-semibold">US Economic</span>
                  </Link>
                </div>

                {/* Mobile Nav */}
                <nav className="flex flex-col px-3 py-3 gap-0.5">
                  {navigation.map((item) => {
                    const active = isActiveRoute(pathname, item.href)
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 text-sm rounded-md transition-colors",
                          active
                            ? "bg-primary/10 text-foreground border-l-2 border-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        )}
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        {item.name}
                      </Link>
                    )
                  })}
                </nav>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  )
}
